/**
 * Parameter descriptor accessor (UI migration Phase 1b, step 1)
 *
 * ONE uniform way to ask any node "what are your parameters?", read over every effect (`EFFECT_SCHEMAS`) plus the display objects. This is a DESCRIPTOR layer:
 * it does not change `live`, the wire, or the <Field> contract. `descriptor.schema` is the raw per-key schema object, ===-identical to what panels already spread onto <Field>.
 *
 * It is the single home for facts that were previously implicit and scattered:
 *   - INTEGER_PARAMS (liveSync)            → descriptor.integer
 *   - IGNORE_PROPS (transitionManager)     → !descriptor.tweenable
 *   - getInitialValue / snap (transition)  → schema.initial / descriptor.integer
 *
 * The descriptor MUST NOT branch identity/tween/discrete logic on node.kind — that is a routing/path fact only (filter vs diffusion is where today's server routes a param, not a property of the param). See spec §Out of scope.
 */
import display_object_schema from '~/schemas/display_object_schema'
import { EFFECT_SCHEMAS } from '~/schemas/effect_schemas'
import { boundParams, getShader, isShaderSource, onShadersChanged, SHADER_NAMES, SHADER_PREFIX } from '~/lib/shaders'
import { isFileSource } from '~/lib/sources/ids'
import video_file_schema from '~/schemas/video_file_schema'
import { FRAME_PARAMS, MIRROR_PARAM } from '~/schemas/source_picture_schema'
import { fillSourcesOf } from '~/lib/sourceFill'

/**
 * Classify a raw per-key schema object into the five descriptor facts. Pure, schema-only — reused by getNodeParams and by the migrated readers.
 *
 * Derivation:
 *   structural = readOnly === true || type === 'array' || const !== undefined || (schema absent) discrete   = !structural && (enum || type ∈ {string, boolean}) continuous = !structural && !discrete && type ∈ {number, integer} integer    = type === 'integer' tweenable  = continuous && tween !== false
 *
 * Notes:
 *   - `integer` IS the contract's own type, not `step === 1`. `step` is how far one arrow-key press moves a slider — a widget fact that says nothing about whether a fractional value is legal, and the two disagree on 33 params (every object `x`/`y`/`opacity`, `control.edges_strength`, `motion.wiggle`, …) which the server accepts as floats. Reading the type is what makes the client and `gen_params.py` answer this from one fact.
 *   - `const` marks a type-discriminator field (`type: { const: 'rect' }`) — always metadata, never a real parameter, so it is structural, not discrete. (Verified: `const` is used only for `type`.)
 *   - `tween: false` means "the generic numeric tween must NOT run on this" — its value is carried by a bespoke path (e.g. the `data` morph resizes a path's width/height), a separate render path (video-fit for zoom/pan), or has no working tween yet. It does NOT mean "never rideable": the prop is still `continuous`.
 *
 * @param {Object|undefined} schema raw per-key schema object (undefined ⇒ key absent ⇒ structural)
 * @returns {{structural:boolean, discrete:boolean, continuous:boolean, integer:boolean, tweenable:boolean}}
 */
export const classifyParam = schema => {
	// Absent from schema ⇒ runtime/structural metadata (e.g. `name`, `maskFor`).
	if (!schema) {
		return { structural: true, discrete: false, continuous: false, integer: false, tweenable: false }
	}

	const type = schema.type
	// A container (`type: 'object'`, e.g. `source_settings`) is storage, never a scalar control. `fill`'s `['string','object']` union is an array, not this literal.
	const structural = schema.readOnly === true || type === 'array' || type === 'object' || schema.const !== undefined
	const discrete = !structural && (schema.enum !== undefined || type === 'string' || type === 'boolean')
	const continuous = !structural && !discrete && (type === 'number' || type === 'integer')
	const integer = continuous && type === 'integer'
	const tweenable = continuous && schema.tween !== false

	return { structural, discrete, continuous, integer, tweenable }
}

/**
 * Absence means at-initial — THE one statement of the rule. Everything else delegates here.
 *
 * ⚠️ Loose `== null`, deliberately: it catches an explicit stored `null` as well as `undefined`. Old presets persist nulls for `opacity`/`flipX`/`locked`/`mask`, and handing one to a reader is a live bug (`setOpacityVars` computes `alpha = null / 100 = 0`). The migration chain strips them at the door; this is the second line.
 */
export const resolve = (value, prop) => (value == null ? prop?.initial : value)

/**
 * A cyclic param's value in the ONE spelling that may be stored — the write-side counterpart of `resolve`, and what lets a prune compare against `initial` at all.
 *
 * ⚠️ THE DOMAIN IS HALF-OPEN, `[minimum, maximum)`. 0° and 360° are one angle, so an inclusive maximum gives one state two spellings and the dirty check reports them as a difference. `useStepper` and `useDragNumber` already wrap unconditionally — stepping up from 359 yields 0.
 *
 * ⚠️ AN IN-RANGE VALUE IS RETURNED UNTOUCHED, and that is not just a fast path: `(v - lo) % span + lo` is not exact in floating point, so running it on an already-canonical 359.40667271523813 returns 359.4066727152381 and the migration is no longer stable — it rewrites stored data on every load.
 *
 * `((x % n) + n) % n` because JS `%` keeps the sign of the dividend.
 */
export const canonical = (prop, value) => {
	if (!prop?.wrap || typeof value !== 'number') return value
	const { minimum: lo, maximum: hi } = prop
	if (typeof lo !== 'number' || typeof hi !== 'number' || hi <= lo) return value
	if (value >= lo && value < hi) return value
	const span = hi - lo
	return lo + ((((value - lo) % span) + span) % span)
}

/** The control a param renders with: an explicit kind, else a select for a closed set, else its value type. The one chooser every param surface reads. */
export const controlFor = (schema, kind) => kind || schema?.kind || (schema?.enum ? 'select' : schema?.type)

const CONTROL_SPAN = { color: 1, fill: 1, boolean: 1, check: 1, select: 1, prompt: 4 }

/** Quarters of a param row a param takes by its control: compact controls one, a prompt the whole row, anything else half. */
export const spanFor = (schema, kind) => CONTROL_SPAN[controlFor(schema, kind)] ?? 2

export const decimalsOf = step => String(step).split('.')[1]?.length ?? 0

/** A value as the operator reads it: through the schema's `display_multiplier` (the 0-999 strength shown as 0-100), at the precision of the scaled step. The wire value is untouched. */
export const toDisplay = (schema, value) => {
	const m = schema?.display_multiplier ?? 1
	const step = (schema?.step || 1) * m
	const v = Number(value) * m
	if (step >= 1) return Math.round(v)
	return Number(v.toFixed(Math.max(0, Math.ceil(-Math.log10(step)))))
}

/* Float arithmetic on a fractional step leaves 0.30000000000000004. Snap to the precision of the step actually applied — alt's step/10 carries one decimal more than step itself, so rounding to step's precision would erase the finer increment entirely. */
export const snap = (v, effectiveStep) => {
	const d = decimalsOf(effectiveStep)
	return d ? Math.round(v * 10 ** d) / 10 ** d : Math.round(v)
}

/**
 * A numeric param's storable value: rounded to the precision its `step` declares, then wrapped.
 *
 * ⚠️ `step ?? 1` IS THE PRECISION, the same derivation the widgets use (`boxesFor`), so what is stored is what the operator can see and type. Without it the transformer's trig stores 1.0880198573681121°.
 *
 * ⚠️ ROUND BEFORE COMPARING TO `initial`, in every caller. A stray 1e-7 fails `=== initial`, so the key is never pruned and the preset reads dirty forever.
 */
export const quantize = (prop, value) => {
	if (!prop || typeof value !== 'number' || !Number.isFinite(value)) return value
	if (prop.type !== 'number' && prop.type !== 'integer') return value
	return canonical(prop, snap(value, prop.step ?? 1))
}

/** What `settleValue` returns for a value that must not be stored. A sentinel, because `undefined` is itself a storable absence and `null` a legacy stored value. */
export const DROP = Symbol('drop')

/**
 * THE WRITE RULE, for one value: quantize, then DROP what equals the contract `initial`.
 *
 * ⚠️ Every writer into `live`, the channel or storage runs this. `selectPresetChanged` diffs on key EXISTENCE, so a writer that skips it strands a dense key and the preset reads permanently dirty.
 */
export const settleValue = (prop, value) => {
	const v = quantize(prop, value)
	if (v === undefined) return v
	// An array never equals its initial by reference once it has been edited or crossed JSON.
	if (Array.isArray(v) && Array.isArray(prop?.initial))
		return JSON.stringify(v) === JSON.stringify(prop.initial) ? DROP : v
	return v === prop?.initial ? DROP : v
}

// --- source_settings: per-source params at preset scope ------------------------------------

// Tempo lock: Speed reads as cycles of the shader's natural period per beat. A control on the Speed row, never a row of its own.
export const SPEED_SYNC = 'speed_sync'
const SHADER_BAG = Object.freeze({
	[SPEED_SYNC]: { type: 'boolean', initial: false, label: 'Tempo lock', tip: 'Speed counts cycles per beat' },
})
const FRAMED_PICTURE = Object.freeze({ ...MIRROR_PARAM, ...FRAME_PARAMS })
const FILE_PARAMS = Object.freeze({ ...FRAMED_PICTURE, ...video_file_schema })
const shaderParams = new WeakMap()

/** A vocabulary pin's source: `source_settings.@shader.<id>` holds `id` on every shader every fill shows. */
export const SHADER_PIN = '@shader'

let sharedParams = null
onShadersChanged(() => {
	sharedParams = null
})
// The ids two or more shaders declare, each with its first declaring shader's schema; an id one shader alone declares pins on that shader.
const sharedShaderParams = () => {
	if (sharedParams) return sharedParams
	const seen = new Set()
	const out = {}
	for (const name of SHADER_NAMES)
		for (const [key, def] of Object.entries(sourceParams(SHADER_PREFIX + name))) {
			if (seen.has(key)) out[key] ??= def
			seen.add(key)
		}
	return (sharedParams = Object.freeze(out))
}

/** The params a source's bag holds: a picture's (mirror; zoom and pan when framed) and a shader's own; `SHADER_PIN`'s are the ids shaders share. Null for no source. */
export const sourceParams = source => {
	if (!source || source === 'none') return null
	if (source === SHADER_PIN) return sharedShaderParams()
	if (!isShaderSource(source)) return isFileSource(source) ? FILE_PARAMS : FRAMED_PICTURE
	const params = getShader(source)?.params
	if (!params) return SHADER_BAG
	let merged = shaderParams.get(params)
	if (!merged) shaderParams.set(params, (merged = Object.freeze({ ...SHADER_BAG, ...params })))
	return merged
}

const EMPTY_VALUES = Object.freeze({})

const RANGE_FIELDS = ['minimum', 'maximum', 'step', 'curve']
const hasRange = def => Number.isFinite(def.minimum) && Number.isFinite(def.maximum)

// The range a subset exposing `id` gives it: the registry declares none for some pipeline ids.
const exposedRange = id => {
	for (const name of SHADER_NAMES) {
		const def = getShader(`${SHADER_PREFIX}${name}`)
		const own = def?.locks && def.params[id]
		if (own && hasRange(own))
			return Object.fromEntries(RANGE_FIELDS.filter(f => own[f] !== undefined).map(f => [f, own[f]]))
	}
	return null
}

// A lock as a control: a vec2 with equal axes is one value, as an exposed `pan` is; a number takes an exposing subset's range when the registry has none. Null when no control fits.
const lockControl = (id, def) => {
	if (def.type !== 'number') return def
	const initial = Array.isArray(def.initial)
		? def.initial.every(v => v === def.initial[0])
			? def.initial[0]
			: NaN
		: def.initial
	if (!Number.isFinite(initial)) return null
	const range = hasRange(def) ? {} : exposedRange(id)
	return range ? { ...def, ...range, initial } : null
}

const lockedDefs = new WeakMap()
/** A subset's locked ids as params in registry order, each at its lock as `initial` (Advanced): one the bag holds is unlocked. An id no control fits is left out. Empty for any other source. */
export const lockedParams = source => {
	const shader = isShaderSource(source) ? getShader(source) : null
	if (!shader?.locks) return EMPTY_VALUES
	let defs = lockedDefs.get(shader)
	if (!defs) {
		const bound = boundParams(shader, { free: Object.keys(shader.locks) })
		const controls = Object.entries(bound).map(([id, def]) => [id, lockControl(id, def)])
		defs = Object.freeze(Object.fromEntries(controls.filter(([, def]) => def)))
		lockedDefs.set(shader, defs)
	}
	return defs
}

/** A source's values in a settings or live state, unlocked ids included; absent keys are at their initial (a locked id at its lock). */
export const sourceValues = (state, source) => state?.source_settings?.[source] ?? EMPTY_VALUES

export const sourceParamPath = (source, key) => `source_settings.${source}.${key}`

/** The pin key of `source`'s `key`: a shared shader id pins as a vocabulary pin, anything else on its own path. */
export const sourcePinKey = (source, key) =>
	isShaderSource(source) && key in sharedShaderParams()
		? sourceParamPath(SHADER_PIN, key)
		: sourceParamPath(source, key)

const VOCAB_PIN_PREFIX = `source_settings.${SHADER_PIN}.`

/** A vocabulary pin key's id; null for any other key. */
export const vocabPinId = key =>
	typeof key === 'string' && key.startsWith(VOCAB_PIN_PREFIX) ? key.slice(VOCAB_PIN_PREFIX.length) : null

/** The vocabulary pins' held values, `{ id: value }`; unread (null) ones left out. */
export const vocabPinValues = layer => {
	const held = layer?.source_settings?.[SHADER_PIN]
	if (!held) return null
	const out = {}
	for (const k in held) if (held[k] != null) out[k] = held[k]
	return Object.keys(out).length ? out : null
}

const sameValues = (a, b) => {
	const ka = Object.keys(a)
	return ka.length === Object.keys(b).length && ka.every(k => JSON.stringify(a[k]) === JSON.stringify(b[k]))
}

/** In place on a settings or live draft: every shader a fill shows takes `held`'s values for the ids it exposes or its bag unlocks, clamped to its own range; a locked id stays locked. Writes nothing that already holds. */
export const fillVocabPins = (state, held) => {
	if (!held) return
	for (const source of fillSourcesOf(state.objects)) {
		if (!isShaderSource(source)) continue
		const params = sourceParams(source)
		const locked = lockedParams(source)
		const current = sourceValues(state, source)
		const ids = Object.keys(held).filter(k => k in params || (k in locked && current[k] !== undefined))
		if (!ids.length) continue
		const next = { ...current }
		for (const k of ids) next[k] = held[k]
		if (!sameValues(settleSourceValues(source, next) ?? {}, current)) writeSourceValues(state, source, next)
	}
}

/** Settle one source's values: clamp to the declared range, drop what equals its initial (an unlocked id its lock), keep undeclared keys (a file's fingerprint). Undefined when nothing survives. */
export const settleSourceValues = (source, values) => {
	if (!values || typeof values !== 'object') return undefined
	const params = sourceParams(source)
	const locked = lockedParams(source)
	const out = {}
	for (const [k, v] of Object.entries(values)) {
		if (v == null) continue
		const prop = params?.[k] ?? locked[k]
		let value = v
		// Conform's clamp pass cannot see inside an opaque bag, so the range repair lives here. A cyclic param wraps in `settleValue` instead.
		if (prop && !prop.wrap && typeof value === 'number') {
			if (typeof prop.minimum === 'number' && value < prop.minimum) value = prop.minimum
			if (typeof prop.maximum === 'number' && value > prop.maximum) value = prop.maximum
		}
		const settled = settleValue(prop, value)
		if (settled !== DROP && settled !== undefined) out[k] = settled
	}
	return Object.keys(out).length ? out : undefined
}

/**
 * THE WRITE RULE for `source_settings`: each entry settled per key; only the sources a fill in `objects` references survive. Undefined when nothing does.
 *
 * ⚠️ Structural, never `settleValue` on the bag: its `{}` initial matches only by reference.
 */
export const settleSourceSettings = (bags, objects) => {
	if (!bags || typeof bags !== 'object' || Array.isArray(bags)) return undefined
	const used = fillSourcesOf(objects)
	const out = {}
	for (const source in bags) {
		if (!used.has(source)) continue
		const values = settleSourceValues(source, bags[source])
		if (values) out[source] = values
	}
	return Object.keys(out).length ? out : undefined
}

/** In place on a draft: drops the entries no fill references; the bag goes when empty. */
export const reapSourceSettings = state => {
	const bags = state.source_settings
	if (!bags) return
	const used = fillSourcesOf(state.objects)
	for (const source of Object.keys(bags)) if (!used.has(source)) delete bags[source]
	if (!Object.keys(bags).length) delete state.source_settings
}

/** In place on a draft: `source`'s entry becomes `values`, settled; an emptied entry goes. */
export const writeSourceValues = (state, source, values) => {
	const settled = settleSourceValues(source, values)
	if (settled) (state.source_settings ??= {})[source] = settled
	else if (state.source_settings) {
		delete state.source_settings[source]
		if (!Object.keys(state.source_settings).length) delete state.source_settings
	}
}

/** `source`'s declared params back at their initial and every unlocked id back at its lock, undeclared keys kept; null when none is set. */
export const sourceResetValues = (state, source) => {
	const params = sourceParams(source)
	const locked = lockedParams(source)
	const values = sourceValues(state, source)
	const declared = k => k in params || k in locked
	if (!params || !Object.keys(values).some(declared)) return null
	return Object.fromEntries(Object.entries(values).filter(([k]) => !declared(k)))
}

// --- schema resolution per node kind ------------------------------------------------------

// Resolve the property-map for a node. Objects pick their definition by the live object's type; every other node kind IS an effect name.
const propsForNode = (node, live) => {
	if (node.kind === 'object') {
		const obj = live?.objects?.[node.id]
		const def = obj && display_object_schema.definitions[obj.type]
		return def ? def.properties : null
	}
	return EFFECT_SCHEMAS[node.kind]?.properties ?? null
}

// The live value slice a node's params live in.
const sliceForNode = (node, live) => {
	if (node.kind === 'object') return live?.objects?.[node.id]
	return EFFECT_SCHEMAS[node.kind] ? live?.[node.kind] : null
}

// The dot-path prefix a node's params live under, for live-prop / dot-prop reads.
const pathPrefix = node => {
	if (node.kind === 'object') return `objects.${node.id}.`
	return EFFECT_SCHEMAS[node.kind] ? `${node.kind}.` : ''
}

/**
 * Look up a parameter's schema-`initial` for an object type — the contract half of `resolve`, for callers that hold a type name rather than a schema object (the per-GSAP-frame tween loop, the Pixi render path).
 *
 * @returns the initial value, or undefined if the type/key is unknown.
 */
export const getObjectInitial = (type, key) => {
	const def = display_object_schema.definitions[type]
	return def?.properties?.[key]?.initial
}

/**
 * Parse a `live` dot-path. ⚠️ The one parser: callers never split on dots themselves.
 *
 * A source param is `source_settings.<source>.<key>`, preset scope: every fill on that source shares it.
 *
 * @returns {{scope: 'effect'|'object'|'source', effect?: string, id?: string, key?: string, existence?: boolean, source?: string}|null}
 */
export const parseLivePath = path => {
	if (typeof path !== 'string') return null
	const parts = path.split('.')
	if (parts[0] === 'objects') {
		// No `key`: the object's existence, none of its props.
		if (parts.length === 2) return { scope: 'object', id: parts[1], existence: true }
		if (parts.length === 3) return { scope: 'object', id: parts[1], key: parts[2] }
		return null
	}
	if (parts[0] === 'source_settings')
		return parts.length === 3 && parts[1] ? { scope: 'source', source: parts[1], key: parts[2] } : null
	if (parts.length !== 2) return null
	return { scope: 'effect', effect: parts[0], key: parts[1] }
}

/** Is `path` held by the pin set? An existence pin holds none of the object's props; a vocabulary pin holds its id on every shader. */
export const isPinned = (livePin, path) => {
	if (!livePin?.length) return false
	if (livePin.includes(path)) return true
	return (
		typeof path === 'string' &&
		path.startsWith(`source_settings.${SHADER_PREFIX}`) &&
		livePin.includes(VOCAB_PIN_PREFIX + path.slice(path.lastIndexOf('.') + 1))
	)
}

/** Is this object's existence pinned? Pinning any prop of it pins its existence too. */
export const isObjectPinned = (livePin, id) => !!livePin?.includes(`objects.${id}`)

/** The schema a `live` dot-path resolves to; an object's comes from its own `type`, a source param's from its source. */
export const schemaForLivePath = (path, objects) => {
	const p = parseLivePath(path)
	if (!p || p.existence) return undefined
	if (p.scope === 'effect') return EFFECT_SCHEMAS[p.effect]?.properties?.[p.key]
	if (p.scope === 'source') return sourceParams(p.source)?.[p.key] ?? lockedParams(p.source)[p.key]
	return display_object_schema.definitions[objects?.[p.id]?.type]?.properties?.[p.key]
}

/**
 * @typedef {Object} ParamDescriptor
 * @property {string} key
 * @property {string} path          dot-path in `live` (e.g. 'diffusion.strength', 'objects.<id>.x')
 * @property {Object} schema        the RAW per-key schema object (unchanged — <Field> spreads it)
 * @property {*}      value         current value from `live`, or schema.initial when absent
 * @property {boolean} structural
 * @property {boolean} discrete
 * @property {boolean} continuous
 * @property {boolean} integer
 * @property {boolean} tweenable
 * @property {{nonDefault:boolean, automated:boolean, bound:boolean, lastTouched:*, inPreset:boolean}} facts
 */

/**
 * Return the uniform parameter list for a node, read over `live`.
 *
 * @param {{kind:string}|{kind:'object', id:string}|{kind:'source', source:string}} node  `kind` is an effect name (any key of EFFECT_SCHEMAS), 'object' or 'source'
 * @param {Object} live  the live state ({ diffusion, motion, control, ip, filter, inpaint, objects, source_settings })
 * @returns {ParamDescriptor[]}
 */
export const getNodeParams = (node, live) => {
	if (node.kind === 'source') {
		const values = sourceValues(live, node.source)
		return Object.entries(sourceParams(node.source) ?? {}).map(([key, schema]) => ({
			...buildDescriptor(
				key,
				sourceParamPath(node.source, key),
				schema,
				resolve(values[key], schema),
				schema.initial
			),
			source: node.source,
		}))
	}
	const props = propsForNode(node, live)
	if (!props) return []

	const slice = sliceForNode(node, live)
	const prefix = pathPrefix(node)
	const instance = node.kind === 'object' ? live?.objects?.[node.id] : null

	const descriptors = []
	for (const key of Object.keys(props)) {
		const schema = props[key]

		const initial = instance ? getObjectInitial(instance.type, key) : schema.initial

		// ⚠️ `resolve`, not a strict presence test. `key in slice ? slice[key] : initial` returned a stored `null` VERBATIM — the black-fade bug — where the loose `== null` resolves it to the initial.
		const value = resolve(slice?.[key], { initial })

		descriptors.push(buildDescriptor(key, prefix + key, schema, value, initial))
	}

	return descriptors
}

const buildDescriptor = (key, path, schema, value, initial) => {
	const facts = classifyParam(schema)
	return {
		key,
		path,
		schema,
		value,
		structural: facts.structural,
		discrete: facts.discrete,
		continuous: facts.continuous,
		integer: facts.integer,
		tweenable: facts.tweenable,
		facts: {
			nonDefault: value !== initial,
			// D30 slots — present even while empty (spec §deferred).
			automated: false, // until Phase 4 (input routing)
			bound: false, // until Phase 4
			lastTouched: undefined, // no reader consumes it this step
			inPreset: false, // no reader consumes it this step
		},
	}
}

/**
 * Build a {key: 1} snap map for the integer params of a schema property-map. Snapping to whole units is a consequence of the contract's `integer` type, not of `step` — a fractional-stepped integer would still have to land on whole numbers. `transitionManager.getSnapConfig` is the one caller, per effect.
 * @param {Object} props a schema `properties` object
 * @returns {Object<string, 1>}
 */
export const buildIntegerSnapConfig = props => {
	const snap = {}
	for (const key in props) {
		if (classifyParam(props[key]).integer) snap[key] = 1
	}
	return snap
}
